import { getImpersonationHeader, getAuthHeader, postJson, getJson, postForm } from './common.js'
import { downloadPdfStream } from './pdfDownload.js'
import { compressPdf } from '@/utils/pdfUtils'

// Portal

export async function fetchPortalInfo(nameUnique) {
  const resJson = await postJson('/forms/portal/info', { nameUnique });
  if (!resJson.portal) throw Error('Malformed response from backend');
  return resJson.portal;
}

// General

export async function fetchEhrFields() {
  return await getJson(`/static/ehrfields.json`)
}

// Forms

export async function createForm(name) {
  const resJson = await postJson('/forms/form/create', { name });
  if (!resJson.form) throw Error('Malformed response from backend');
  return resJson.form;
}

export async function uploadPdfForm(name, file) {
  const formData = new FormData();
  formData.append('name', name);
  formData.append('file', file);
  const resJson = await postForm('/forms/form/upload', formData);
  if (!resJson.form) throw Error('Malformed response from backend');
  return resJson.form;
}

export async function aiConvertForm(name, file) {
  const compressedFile = await compressPdf(file);
  const formData = new FormData();
  formData.append('name', name);
  formData.append('file', compressedFile);
  const resJson = await postForm('/forms/form/ai-convert', formData);
  if (!resJson.jobId) throw Error('Malformed response from backend');
  return { jobId: resJson.jobId, jobs: resJson.jobs || [] };
}

export async function getAiConvertStatus() {
  const resJson = await postJson('/forms/form/ai-convert-status', {});
  if (!resJson.jobs) throw Error('Malformed response from backend');
  return resJson.jobs;
}

export async function duplicateForm(formId) {
  const resJson = await postJson('/forms/form/duplicate', { id: formId });
  if (!resJson.form) throw Error('Malformed response from backend');
  return resJson.form;
}

export async function archiveForm(formId) {
  await postJson('/forms/form/archive', { id: formId });
}

export async function listForms() {
  const resJson = await postJson('/forms/form/list', {});
  if (!resJson.forms) throw Error('Malformed response from backend');
  return resJson.forms;
}

export async function fetchFormInfo(formId) {
  const resJson = await postJson('/forms/form/info', { id: formId });
  if (!resJson.form) throw Error('Malformed response from backend');
  return { ...resJson.form, formsWelcomeMenuMode: resJson.formsWelcomeMenuMode, clientIp: resJson.clientIp || null };
}

export async function updateForm(formId, name, rows, boxes) {
  const body = { id: formId, name: name };
  if (rows !== undefined) body.rows = rows;
  if (boxes !== undefined) body.boxes = boxes;
  const resJson = await postJson('/forms/form/update', body);
  if (!resJson.form) throw Error('Malformed response from backend');
  return resJson.form;
}

// Streaming download (see downloadPdfStream): progress frames, then the blank form PDF as base64 chunks. Resolves with
// the PDF Blob; an AbortSignal cancels mid-stream.
export async function downloadFormStream(formId, onProgress, signal = null) {
  return downloadPdfStream('/forms/form/download-stream', { formId }, onProgress, signal);
}

export async function fetchImportJobs() {
  const resJson = await postJson('/forms/importjob/list', undefined);
  if (!resJson.importJobs) throw Error('Malformed response from backend');
  return resJson.importJobs;
}

export async function renameForm(formId, name) {
  const resJson = await postJson('/forms/form/rename', { id: formId, name });
  if (!resJson.form) throw Error('Malformed response from backend');
  return resJson.form;
}

export async function reorderForms(formIds) {
  await postJson('/forms/form/reorder', { formIds });
}

export async function checkShortCode(shortCode, type, excludeId) {
  const body = { shortCode, type };
  if (excludeId) body.excludeId = excludeId;
  const resJson = await postJson('/forms/shortcode/check', body);
  return resJson.available;
}

export async function setFormShortCode(formId, shortCode) {
  const resJson = await postJson('/forms/form/set-short-code', { id: formId, shortCode });
  if (resJson.error) throw Error(resJson.error);
  if (!resJson.form) throw Error('Malformed response from backend');
  return resJson.form;
}

export async function setPacketShortCode(packetId, shortCode) {
  const resJson = await postJson('/forms/packet/set-short-code', { id: packetId, shortCode });
  if (resJson.error) throw Error(resJson.error);
  if (!resJson.packet) throw Error('Malformed response from backend');
  return resJson.packet;
}

export async function resolveShortCode(shortCode, type) {
  const resJson = await postJson('/forms/shortcode/resolve', { shortCode, type });
  if (resJson.error) throw Error(resJson.error);
  return resJson;
}

// Packets

export async function renamePacket(packetId, name) {
  const resJson = await postJson('/forms/packet/rename', { packetId, name });
  if (!resJson.packet) throw Error('Malformed response from backend');
  return resJson.packet;
}

export async function createPacket(name, formIds) {
  const resJson = await postJson('/forms/packet/create', { name, formIds });
  if (!resJson.packet) throw Error('Malformed response from backend');
  return resJson.packet;
}

export async function updatePacket(packetId, name, formIds) {
  const resJson = await postJson('/forms/packet/update', { packetId, name, formIds });
  if (!resJson.packet) throw Error('Malformed response from backend');
  return resJson.packet;
}

export async function fetchPacketInfo(packetId) {
  const resJson = await postJson('/forms/packet/info', { packetId });
  if (!resJson.packet) throw Error('Malformed response from backend');
  return { ...resJson.packet, formsWelcomeMenuMode: resJson.formsWelcomeMenuMode, clientIp: resJson.clientIp || null };
}

export async function archivePacket(packetId) {
  await postJson('/forms/packet/archive', { packetId });
}

export async function reorderPackets(packetIds) {
  await postJson('/forms/packet/reorder', { packetIds });
}

export async function listPackets() {
  const resJson = await postJson('/forms/packet/list', {});
  if (!resJson.packets) throw Error('Malformed response from backend');
  return resJson.packets;
}

// Streaming download (see downloadPdfStream): progress frames, then the packet's blank forms as one PDF in base64
// chunks. Resolves with the PDF Blob; an AbortSignal cancels mid-stream.
export async function downloadPacketStream(packetId, onProgress, signal = null) {
  return downloadPdfStream('/forms/packet/download-stream', { packetId }, onProgress, signal);
}

// Submissions

export async function createDraftSubmission({ patientFirstName, patientLastName, patientDateOfBirth, patientPreferredLanguage, patientPhone, patientHomePhone, patientWorkPhone, patientEmail, emrIntegrationId, preFillPatientData }) {
  const body = {};
  if (patientFirstName) body.patientFirstName = patientFirstName;
  if (patientLastName) body.patientLastName = patientLastName;
  if (patientDateOfBirth) body.patientDateOfBirth = patientDateOfBirth;
  if (patientPreferredLanguage) body.patientPreferredLanguage = patientPreferredLanguage;
  if (patientPhone) body.patientPhone = patientPhone;
  if (patientHomePhone) body.patientHomePhone = patientHomePhone;
  if (patientWorkPhone) body.patientWorkPhone = patientWorkPhone;
  if (patientEmail) body.patientEmail = patientEmail;
  if (emrIntegrationId) body.emrIntegrationId = emrIntegrationId;
  if (preFillPatientData) body.preFillPatientData = preFillPatientData;
  const resJson = await postJson('/forms/submission/create-draft', body);
  if (!resJson.submission) throw Error('Malformed response from backend');
  return resJson.submission;
}

export async function addFormToSubmission(submissionId, formId) {
  const resJson = await postJson('/forms/submission/add-form', { submissionId, formId });
  if (!resJson.submission) throw Error('Malformed response from backend');
  return resJson.submission;
}

export async function sendSubmission(submissionId, language = null, contactMethods = null) {
  const payload = { submissionId };
  if (language) payload.language = language;
  if (contactMethods && contactMethods.length > 0) payload.contactMethods = contactMethods;
  const resJson = await postJson('/forms/submission/send', payload);
  if (!resJson.submission) throw Error('Malformed response from backend');
  return resJson.submission;
}

export async function resendSubmission(submissionId, contactMethods = null) {
  const payload = { submissionId };
  if (contactMethods && contactMethods.length > 0) payload.contactMethods = contactMethods;
  const resJson = await postJson('/forms/submission/resend', payload);
  if (!resJson.submission) throw Error('Malformed response from backend');
  return resJson.submission;
}

export async function fetchSubmissionTagCounts(hideResolved) {
  const resJson = await postJson('/forms/submission/tag-counts', { hideResolved });
  if (!resJson.tagCounts) throw Error('Malformed response from backend');
  return resJson.tagCounts;
}

export async function searchSubmissions(query, afterSubmissionId, hideResolved, isSubmitted, formId, filterStatus, searchType, tagIds, sortOrder) {
  const body = { query, afterSubmissionId, hideResolved, isSubmitted };
  if (formId) body.formId = formId;
  if (filterStatus) body.filterStatus = filterStatus;
  if (searchType) body.searchType = searchType;
  if (tagIds && tagIds.length > 0) body.tagIds = tagIds;
  if (sortOrder && sortOrder !== 'desc') body.sortOrder = sortOrder;
  const resJson = await postJson('/forms/submission/search', body);
  if (!resJson.submissions) throw Error('Malformed response from backend');
  return resJson;
}

export async function fetchSubmissionInfoById(submissionId) {
  const resJson = await postJson('/forms/submission/info-id', { submissionId });
  if (!resJson.submission) throw Error('Malformed response from backend');
  return resJson.submission;
}

export async function prefillEmrSubmission(submissionId, patientData) {
  const body = { submissionId };
  if (patientData) body.patientData = patientData;
  const resJson = await postJson('/forms/submission/prefill-emr', body);
  return resJson.values || null;
}

export async function fetchSubmissionInfoByPrivateURL(privateURL) {
  const resJson = await postJson('/forms/submission/info-url', { privateURL });
  if (!resJson.submission) throw Error('Malformed response from backend');
  return { ...resJson.submission, formsWelcomeMenuMode: resJson.formsWelcomeMenuMode, clientIp: resJson.clientIp || null };
}

export async function createSubmission({ formId, formIds, packetId, patientFirstName, patientLastName, patientDateOfBirth, fillerName, formOpenedAt }) {
  const body = {};
  if (patientFirstName) body.patientFirstName = patientFirstName;
  if (patientLastName) body.patientLastName = patientLastName;
  if (patientDateOfBirth) body.patientDateOfBirth = patientDateOfBirth;
  if (fillerName) body.fillerName = fillerName;
  if (formId) body.formId = formId;
  if (formIds) body.formIds = formIds;
  if (packetId) body.packetId = packetId;
  if (formOpenedAt) body.formOpenedAt = formOpenedAt;
  const resJson = await postJson('/forms/submission/create', body);
  if (!resJson.privateURL) throw Error('Malformed response from backend');
  return { privateURL: resJson.privateURL, clientIp: resJson.clientIp || null };
}

export async function updateSubmissionByPrivateURL(privateURL, values, patientData = {}, language = null, formVersionId = null) {
  const body = {
    privateURL,
    values,
    ...patientData
  };
  if (language) {
    body.language = language;
  }
  if (formVersionId) {
    body.formVersionId = formVersionId;
  }
  await postJson('/forms/submission/update-url', body);
}

export async function updateSubmissionById(submissionId, values, formVersionId = null) {
  const body = { submissionId, values };
  if (formVersionId) {
    body.formVersionId = formVersionId;
  }
  const resJson = await postJson('/forms/submission/update-id', body);
  if (!resJson.values) throw Error('Malformed response from backend');
  return resJson.values;
}

export async function submitSubmissionByPrivateURL(privateURL, language = null) {
  const body = { privateURL };
  if (language) {
    body.language = language;
  }
  const resJson = await postJson('/forms/submission/submit-url', body);
  return resJson.signatureAudit || null;
}

export async function verifySubmissionIntegrity(submissionId) {
  const resJson = await postJson('/forms/submission/verify-integrity', { submissionId });
  if (!resJson.integrity) throw Error('Malformed response from backend');
  return resJson.integrity;
}

export async function uploadFileToSubmission(privateURL, file) {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('privateURL', privateURL);

  const uploadHeaders = {};
  const uploadAuth = getAuthHeader();
  if (uploadAuth) uploadHeaders['Authorization'] = uploadAuth;

  const res = await fetch(process.env.VUE_APP_API + '/forms/submission/upload-file-url', {
    credentials: 'include',
    method: 'POST',
    headers: uploadHeaders,
    body: formData,
  });

  const rawText = await res.text();
  let json = null;
  try { json = JSON.parse(rawText); } catch { /* response wasn't JSON */ }

  if (res.status !== 200) {
    throw new Error(json?.message || res.statusText);
  }

  if (!json?.file) throw Error('Malformed response from backend');
  return json.file;
}

export async function downloadSubmissionForm(submissionId, formVersionId, formVersionIds) {
  const body = { submissionId };
  if (formVersionId) {
    body.formVersionId = formVersionId;
  }
  if (formVersionIds && formVersionIds.length > 0) {
    body.formVersionIds = formVersionIds;
  }

  const headers = { 'Content-Type': 'application/json' };
  const imp = getImpersonationHeader();
  if (imp) headers['X-DQ-Impersonation'] = imp;
  const auth = getAuthHeader();
  if (auth) headers['Authorization'] = auth;

  const res = await fetch(process.env.VUE_APP_API + '/forms/submission/download-pdf', {
    credentials: 'include',
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });

  // Handle 401 - Permission Denied
  if (res.status == 401) {
    throw Error('Permission denied');
  }

  if (res.status != 200) {
    throw Error('Download failed');
  }

  const blob = await res.blob();
  return blob;
}

// Streaming download (see downloadPdfStream) of the submission's forms (only formVersionIds when given) as one PDF.
// Resolves with the PDF Blob; an AbortSignal cancels mid-stream.
export async function downloadSubmissionStream(submissionId, formVersionIds, onProgress, signal = null) {
  const body = { submissionId };
  if (formVersionIds && formVersionIds.length > 0) {
    body.formVersionIds = formVersionIds;
  }
  return downloadPdfStream('/forms/submission/download-stream', body, onProgress, signal);
}

export async function fetchSubmissionEvents(submissionId) {
  const resJson = await postJson('/forms/submission/events', { submissionId });
  if (!resJson.events) throw Error('Malformed response from backend');
  return { events: resJson.events, users: resJson.users || {}, formVersionNames: resJson.formVersionNames || {}, eventsVersion: resJson.eventsVersion || 1 };
}

export async function markSubmissionAsImported(submissionId, importLogs = []) {
  const resJson = await postJson('/forms/submission/mark-imported', { submissionId, importLogs });
  return resJson;
}

export async function deleteSubmission(submissionId) {
  const resJson = await postJson('/forms/submission/delete', { submissionId });
  return resJson;
}

export async function discardDraft(submissionId) {
  const resJson = await postJson('/forms/submission/discard-draft', { submissionId });
  return resJson;
}

export async function removeSubmissionForms(submissionId, formVersionIds) {
  const resJson = await postJson('/forms/submission/remove-forms', { submissionId, formVersionIds });
  if (!resJson.submission) throw Error('Malformed response from backend');
  return resJson.submission;
}

export async function reorderSubmissionForms(submissionId, formVersionIds) {
  const resJson = await postJson('/forms/submission/reorder-forms', { submissionId, formVersionIds });
  if (!resJson.submission) throw Error('Malformed response from backend');
  return resJson.submission;
}

export async function submitRating(privateURL, rating, feedbackReason) {
  const resJson = await postJson('/forms/submission/submit-rating', { privateURL, rating, feedbackReason });
  return resJson;
}

export async function downloadSubmissionFile(submissionFileId) {
  const headers = { 'Content-Type': 'application/json' };
  const imp = getImpersonationHeader();
  if (imp) headers['X-DQ-Impersonation'] = imp;
  const auth = getAuthHeader();
  if (auth) headers['Authorization'] = auth;

  const res = await fetch(process.env.VUE_APP_API + '/forms/submission/download-file', {
    credentials: 'include',
    method: 'POST',
    headers,
    body: JSON.stringify({ submissionFileId }),
  });

  // Handle 401 - Permission Denied
  if (res.status == 401) {
    throw Error('Permission denied');
  }

  // Handle 403 - Forbidden
  if (res.status == 403) {
    throw Error('You do not have permission to download this file');
  }

  // Handle 404 - Not Found
  if (res.status == 404) {
    throw Error('File not found');
  }

  const blob = await res.blob();
  const filename = res.headers.get('Content-Disposition')?.split('filename="')[1]?.slice(0, -1) || 'download';
  return { blob, filename };
}

export async function getSubmissionFilePreviewUrl(submissionFileId) {
  const headers = { 'Content-Type': 'application/json' };
  const imp = getImpersonationHeader();
  if (imp) headers['X-DQ-Impersonation'] = imp;
  const auth = getAuthHeader();
  if (auth) headers['Authorization'] = auth;

  const res = await fetch(process.env.VUE_APP_API + '/forms/submission/download-file', {
    credentials: 'include',
    method: 'POST',
    headers,
    body: JSON.stringify({ submissionFileId }),
  });

  // Handle errors
  if (res.status == 401) {
    throw Error('Permission denied');
  }
  if (res.status == 403) {
    throw Error('You do not have permission to view this file');
  }
  if (res.status == 404) {
    throw Error('File not found');
  }
  // Catch-all: never blob a non-200 body (e.g. a 400/500 JSON error) into a broken object URL
  if (res.status != 200) {
    throw Error('Failed to load file preview');
  }

  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

export async function getSubmissionFileThumbnailUrl(submissionFileId) {
  const headers = { 'Content-Type': 'application/json' };
  const imp = getImpersonationHeader();
  if (imp) headers['X-DQ-Impersonation'] = imp;
  const auth = getAuthHeader();
  if (auth) headers['Authorization'] = auth;

  const res = await fetch(process.env.VUE_APP_API + '/forms/submission/download-thumbnail', {
    credentials: 'include',
    method: 'POST',
    headers,
    body: JSON.stringify({ submissionFileId }),
  });

  // Handle errors - 404 is expected for files without thumbnails, don't throw
  if (res.status == 401) {
    throw Error('Permission denied');
  }
  if (res.status == 403) {
    throw Error('You do not have permission to view this file');
  }
  if (res.status == 404) {
    return null; // No thumbnail available, caller should fallback to full image
  }
  // Catch-all: never blob a non-200 body (e.g. a 400/500 JSON error) into a broken object URL
  if (res.status != 200) {
    return null; // fall back to the full-file preview
  }

  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

// Public (privateURL token) variant of getSubmissionFileThumbnailUrl — used by the patient-facing
// submission filler, which has no authenticated user. Returns null when there is no thumbnail.
export async function getSubmissionFileThumbnailUrlByPrivateURL(privateURL, submissionFileId) {
  const res = await fetch(process.env.VUE_APP_API + '/forms/submission/download-thumbnail-url', {
    credentials: 'include',
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ privateURL, submissionFileId }),
  });

  // 404 is expected for files without thumbnails - return null so the caller falls back to the full image
  if (res.status == 404) {
    return null;
  }
  if (res.status == 400 || res.status == 403) {
    throw Error('You do not have permission to view this file');
  }
  // Catch-all: never blob a non-200 body (e.g. a 500) into a broken object URL
  if (res.status != 200) {
    return null; // fall back to the full-file preview
  }

  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

// Public (privateURL token) variant of getSubmissionFilePreviewUrl — fallback when a file has no thumbnail.
export async function getSubmissionFilePreviewUrlByPrivateURL(privateURL, submissionFileId) {
  const res = await fetch(process.env.VUE_APP_API + '/forms/submission/download-file-url', {
    credentials: 'include',
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ privateURL, submissionFileId }),
  });

  if (res.status == 400 || res.status == 403 || res.status == 404) {
    throw Error('You do not have permission to view this file');
  }
  // Catch-all: never blob a non-200 body (e.g. a 500) into a broken object URL
  if (res.status != 200) {
    throw Error('Failed to load file preview');
  }

  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

export async function fetchEmrFields(emrName) {
  const fields = await getJson(`/static/emrs/${emrName}.json`);
  return fields;
}

// Templates

export async function publishFormTemplate(name, description, rows, boxes, { type, pdfPageCount, pdfPagesVersionId } = {}) {
  const body = { name, description, rows };
  if (boxes) body.boxes = boxes;
  if (type) body.type = type;
  if (pdfPageCount) body.pdfPageCount = pdfPageCount;
  if (pdfPagesVersionId) body.pdfPagesVersionId = pdfPagesVersionId;
  const resJson = await postJson('/forms/template/create', body);
  if (!resJson.template) throw Error('Malformed response from backend');
  return resJson.template;
}

export async function listFormTemplates() {
  const resJson = await postJson('/forms/template/list', undefined);
  if (!resJson.templates) throw Error('Malformed response from backend');
  return resJson.templates;
}

export async function searchFormTemplates(query) {
  const resJson = await postJson('/forms/template/search', { query });
  if (!resJson.templates) throw Error('Malformed response from backend');
  return resJson.templates;
}

export async function useFormTemplate(templateId, name) {
  const resJson = await postJson('/forms/template/use', { templateId, name });
  if (!resJson.form) throw Error('Malformed response from backend');
  return resJson.form;
}

export async function fetchFormTemplateInfo(templateId) {
  const resJson = await postJson('/forms/template/info', { id: templateId });
  if (!resJson.template) throw Error('Malformed response from backend');
  return resJson.template;
}

export async function unpublishFormTemplate(templateId) {
  await postJson('/forms/template/delete', { id: templateId });
}

export async function updateSubmissionNote(submissionId, note) {
  const resJson = await postJson('/forms/submission/update-note', { submissionId, note });
  if (!resJson.submission) throw Error('Malformed response from backend');
  return resJson.submission;
}

export async function markSubmissionResolved(submissionId, isResolved) {
  const resJson = await postJson('/forms/submission/mark-resolved', { submissionId, isResolved });
  if (!resJson.submission) throw Error('Malformed response from backend');
  return resJson.submission;
}

// Tags

export async function listFormsTags() {
  const resJson = await postJson('/forms/tag/list', {});
  if (!resJson.tags) throw Error('Malformed response from backend');
  return resJson.tags;
}

export async function createFormsTag(name, color, description = '') {
  const resJson = await postJson('/forms/tag/create', { name, color, description });
  if (!resJson.tag) throw Error('Malformed response from backend');
  return resJson.tag;
}

export async function updateFormsTag(tagId, name, color, description = '') {
  const resJson = await postJson('/forms/tag/update', { tagId, name, color, description });
  if (!resJson.tag) throw Error('Malformed response from backend');
  return resJson.tag;
}

export async function deleteFormsTag(tagId) {
  await postJson('/forms/tag/remove', { tagId });
}

export async function updateSubmissionTag(submissionId, tagId) {
  const resJson = await postJson('/forms/submission/update-tag', { submissionId, tagId });
  if (!resJson.submission) throw Error('Malformed response from backend');
  return resJson.submission;
}

export async function fetchSubmissionIndicators() {
  const resJson = await getJson('/forms/submission/indicators');
  if (!resJson.indicators) throw Error('Malformed response from backend');
  return resJson.indicators;
}
